import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, FileSpreadsheet, Download, Check, X, AlertTriangle, Loader2,
  History, ArrowRight, ListChecks, Undo2,
} from 'lucide-react';
import { api, leerToken } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { useAvisos } from '../lib/contextoAvisos';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import CatalogoDeLugares from '../importador/CatalogoDeLugares';
import Ayuda from '../ui/Ayuda';
import { useCambiosSinGuardar } from '../ui/navegacionReact';
import TiempoRelativo from '../ui/TiempoRelativo';
import { etiquetaRol } from '../lib/roles';

// TRAER LO QUE YA TIENES, SIN TECLEARLO.
//
// LA PANTALLA ES DE DOS PASOS Y NO DE UNO, por condición del dueño y
// porque una carga masiva es la operación con más alcance por clic de
// todo el sistema: se sube el archivo, se ve QUÉ HARÍA, y solo entonces
// se confirma. Entre los dos pasos no se escribe ni una fila.
//
// LO QUE SE ENSEÑA EN LA VISTA PREVIA no es «480 filas válidas»: son los
// tres grupos por separado, con las primeras filas de cada uno a la
// vista. Un número solo no deja ver que las 120 rechazadas son todas de
// la misma sucursal — y ese patrón es lo que hace falta para arreglar el
// archivo en vez de renunciar a esas 120.

const TIPOS = [
  ['CLIENTES', 'Clientes', 'A quién le vendes: nombre, RFC, teléfono, crédito.'],
  ['PRODUCTOS', 'Productos', 'Tu catálogo: SKU, nombre, precios, mínimos. Sin existencias.'],
  // LAS EXISTENCIAS VAN JUSTO DEBAJO DE PRODUCTOS, y en ese orden a
  // propósito: es la secuencia real de quien empieza. Primero el
  // catálogo, después cuántas piezas hay de cada cosa — y el subtítulo
  // lo dice, porque subirlas al revés rechaza el archivo entero.
  ['UBICACIONES', 'Ubicaciones',
   'Tu almacén: pasillos, racks, niveles. La columna «Depende de» arma el árbol.'],
  ['EXISTENCIAS', 'Existencias iniciales',
   'Cuántas piezas hay de cada producto y en qué ubicación. Sube antes los productos.'],
  ['PROVEEDORES', 'Proveedores', 'A quién le compras: nombre, RFC, contacto y en cuántos días te surte.'],
  ['INSTRUMENTOS', 'Instrumentos de medición', 'Verniers, micrómetros, básculas — y cada cuántos meses toca calibrarlos.'],
  ['DOCUMENTOS', 'Lista maestra de documentos', 'Códigos, títulos y próxima revisión. Los archivos se suben aparte.'],
  ['PERSONAS', 'Tu gente', 'Nombre, teléfono y puesto. TEGO crea una invitación por persona; cada quien pone su PIN.'],
  ['PARTES_CLIENTE', 'Números de parte de tus clientes', 'Con qué código le llama tu cliente a cada uno de tus productos.'],
];

export default function Importar() {
  const [tipo, setTipo] = useState('CLIENTES');
  const [previa, setPrevia] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState('');
  const [historial, setHistorial] = useState([]);
  const [codigos, setCodigos] = useState(null);
  // EL ENSAYO LARGO (más de 2 000 filas) sigue en el servidor y aquí se
  // consulta cada segundo: filas leídas de cuántas. Nunca una rueda sin
  // número (usuario cero, punto 11).
  const [enCurso, setEnCurso] = useState(null);
  const entrada = useRef(null);
  const avisos = useAvisos();
  // IMPORTACIÓN A MEDIAS · TC-DR-AT. Un archivo ya leído y sin aplicar, o
  // los códigos de invitación a la vista, hacen que atrás pregunte.
  useCambiosSinGuardar(() => !!previa || !!enCurso);
  useCambiosSinGuardar(() => !!codigos, {
    detalle: 'Los códigos de invitación se muestran una sola vez. Descárgalos antes de salir.',
  });

  const cargarHistorial = useCallback(async () => {
    try {
      setHistorial(await api.get('/importar'));
    } catch {
      /* el historial es contexto, no bloquea importar */
    }
  }, []);

  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  useEffect(() => {
    if (!enCurso || enCurso.estado !== 'ANALIZANDO') return undefined;
    let vivo = true;
    const t = setInterval(async () => {
      try {
        const a = await api.get(`/importar/${enCurso.id}/avance`);
        if (!vivo) return;
        if (a.estado === 'LISTO') {
          const d = await api.get(`/importar/${enCurso.id}`);
          if (!vivo) return;
          setEnCurso(null);
          setPrevia(d);
        } else if (a.estado === 'FALLO') {
          setEnCurso(null);
          setError(a.error || 'No se pudo leer el archivo.');
        } else {
          setEnCurso((e) => ({ ...e, ...a }));
        }
      } catch (e) {
        if (vivo) { setEnCurso(null); setError(e.message); }
      }
    }, 1000);
    return () => { vivo = false; clearInterval(t); };
  }, [enCurso]);

  async function subir(archivo) {
    if (!archivo) return;
    setSubiendo(true);
    setError('');
    setPrevia(null);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      const r = await fetch(`/api/importar/vista-previa?tipo=${tipo}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${leerToken()}` },
        body: cuerpo,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detalle || 'No se pudo leer el archivo.');
      if (r.status === 202 && d.en_segundo_plano) {
        setEnCurso({ id: d.id, ...d.avance });
        return;
      }
      setPrevia(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubiendo(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  async function aplicar() {
    setAplicando(true);
    try {
      const r = await api.post(`/importar/${previa.id}/aplicar`, {});
      avisos.exito(r.mensaje);
      // LOS CÓDIGOS DE INVITACIÓN SE VEN UNA SOLA VEZ. Si se pierden hay
      // que cancelar las invitaciones y volver a empezar, así que se
      // quedan en pantalla hasta que quien importó los descargue.
      setCodigos(r.entregar?.length ? r.entregar : null);
      setPrevia(null);
      cargarHistorial();
    } catch (e) {
      setError(e.message);
    } finally {
      setAplicando(false);
    }
  }

  async function descartar() {
    try {
      await api.del(`/importar/${previa.id}`);
    } catch { /* da igual: no entró nada */ }
    setPrevia(null);
    cargarHistorial();
  }

  return (
    <div className="flex flex-col gap-5 p-4 max-w-4xl">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">
          Traer lo que ya tienes
        </h1>
        <p className="text-gray-600">
          Sube tu lista desde Excel o desde el sistema que usabas antes. Te
          enseño qué haría con cada fila <strong>antes</strong> de guardar nada.
        </p>
        <Ayuda id="importador.pantalla" />
      </header>

      {!previa && (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-gray-700">
              ¿Qué vas a traer?
            </span>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {TIPOS.map(([id, etiqueta, ayuda]) => (
                <button key={id} type="button" onClick={() => setTipo(id)}
                        aria-pressed={tipo === id}
                        className={`rounded-xl border p-3 text-left ${
                          tipo === id
                            ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]'
                            : 'border-gray-200'}`}>
                  <span className="font-semibold text-gray-900">{etiqueta}</span>
                  <span className="block text-sm text-gray-600">{ayuda}</span>
                </button>
              ))}
            </div>
            {/* LA REGLA DURA, PEGADA AL BOTÓN QUE LA EJECUTA. Subir las
                existencias antes que los productos rechaza el archivo
                entero, y eso hay que saberlo ANTES de elegir el archivo,
                no en la vista previa. */}
            {tipo === 'EXISTENCIAS' && (
              <Ayuda id="importador.existencias_orden" />
            )}
          </div>

          <NotaExplicativa titulo="No hace falta que el archivo esté ordenado">
            Las columnas pueden venir en cualquier orden y llamarse como se
            llamen en tu sistema —«R.F.C.», «Razón Social», «Tel. 1»—. Los
            espacios de sobra, los acentos y los importes con signo de pesos se
            arreglan solos. Lo que no entienda, te lo digo.
          </NotaExplicativa>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button" onClick={() => entrada.current?.click()}
              disabled={subiendo}
              className="inline-flex items-center gap-2 rounded-xl
                         bg-[var(--color-marca)] px-4 py-3 font-semibold text-white
                         disabled:bg-gray-300">
              {subiendo
                ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                : <Upload className="h-5 w-5" aria-hidden="true" />}
              {subiendo ? 'Leyendo tu archivo…' : 'Elegir archivo'}
            </button>
            <input
              ref={entrada} type="file" className="hidden"
              accept=".csv,.xlsx,text/csv"
              onChange={(e) => subir(e.target.files?.[0])} />

            <button
              type="button"
              onClick={async () => {
                try {
                  guardarArchivo(
                    await api.bajar(`/importar/plantilla?tipo=${tipo}`),
                    `plantilla-${tipo.toLowerCase()}.xlsx`);
                } catch (e) { setError(e.message); }
              }}
              className="inline-flex items-center gap-2 text-sm font-semibold
                         text-[var(--color-marca)]">
              <Download className="h-4 w-4" aria-hidden="true" />
              Descargar la plantilla
            </button>
            <span className="text-sm text-gray-500">
              Acepta Excel (.xlsx) y CSV.
            </span>
          </div>

          {enCurso && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm
                            text-blue-900" role="status" aria-live="polite">
              <p className="font-semibold">
                Leyendo tu archivo en el servidor: fila {enCurso.filas} de {enCurso.total}
              </p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-blue-100">
                <div className="h-2 bg-[var(--color-marca)] transition-all"
                     style={{ width: `${enCurso.total ? Math.round((100 * enCurso.filas) / enCurso.total) : 0}%` }} />
              </div>
              <p className="mt-1 text-blue-800">
                Puedes seguir en esta pantalla; cuando termine, verás qué haría
                con cada fila.
              </p>
            </div>
          )}

          {error && (
            <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50
                            p-3 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {codigos && <CodigosDeInvitacion filas={codigos}
                                           onCerrar={() => setCodigos(null)} />}

          <Historial filas={historial} onRecargar={cargarHistorial} />

          {/* EL CATALOGO DE LUGARES VA AQUI, DEBAJO Y PLEGADO. Es
              material de referencia, no un paso de la tarea: quien
              viene a subir su archivo no tiene que tropezarse con
              él, y quien viene a comprobar si su municipio existe
              lo encuentra sin salir de «traer lo que ya tienes». */}
          <CatalogoDeLugares />
        </>
      )}

      {previa && (
        <VistaPrevia
          d={previa} aplicando={aplicando} onCorregir={setPrevia}
          onAplicar={aplicar} onDescartar={descartar} error={error} />
      )}
    </div>
  );
}

// ------------------------------------------------------- vista previa
function VistaPrevia({ d, aplicando, onAplicar, onDescartar, error, onCorregir }) {
  const t = d.totales;
  // EL MISMO ARCHIVO NO ENTRA DOS VECES. Si ya se aplicó, el botón no
  // existe: la nota de arriba dice cuándo entró y qué hacer.
  const yaEntro = Boolean(d.ya_aplicado);
  const nada = (t.crear === 0 && t.actualizar === 0) || yaEntro;
  const vence = d.ensayo_vence_en ? new Date(d.ensayo_vence_en) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
        <p className="font-semibold text-amber-900">
          No se ha guardado nada.
        </p>
        <p className="text-sm text-amber-800">
          Esto es lo que haría con tu archivo. Revísalo y confirma abajo.
          {vence && (
            <>
              {' '}Este ensayo vale hasta las{' '}
              {vence.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}:
              después habrá que volver a subir el archivo, porque lo que ves
              describe los datos de ahora.
            </>
          )}
        </p>
      </div>

      {yaEntro && (
        <div className="flex flex-col gap-1 rounded-xl border border-blue-200 bg-blue-50
                        p-3 text-sm text-blue-900">
          <span className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{d.ya_aplicado.mensaje}</span>
          </span>
          <Ayuda id="importador.mismo_archivo" />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Tarjeta n={t.crear} etiqueta="se darían de alta" color="verde" />
        <Tarjeta n={t.actualizar} etiqueta="se actualizarían" color="azul" />
        <Tarjeta n={t.rechazar} etiqueta="no entrarían" color="rojo" />
      </div>

      {d.encabezados_en_fila > 1 && (
        <NotaExplicativa titulo={`Entendí que tus columnas están en la fila ${d.encabezados_en_fila}`}>
          Las {d.encabezados_en_fila - 1} filas de arriba —el título, la
          fecha— las salté. Si me equivoqué, quita esas filas del archivo y
          vuelve a subirlo.
        </NotaExplicativa>
      )}

      {d.ejemplos_saltados > 0 && (
        <NotaExplicativa titulo={`Salté ${d.ejemplos_saltados} filas de ejemplo`}>
          Son las que empiezan con la palabra EJEMPLO en la plantilla. No
          entran; puedes dejarlas o borrarlas.
        </NotaExplicativa>
      )}

      {d.columnas_ignoradas?.length > 0 && (
        <MapeoManual d={d} onCorregir={onCorregir} />
      )}

      {d.arbol?.length > 0 && (
        <>
          <ArbolResultante arbol={d.arbol} />
          <Ayuda id="importador.ruta" />
        </>
      )}

      {d.sin_identificador > 0 && (
        <NotaExplicativa titulo={`${d.sin_identificador} sin RFC ni código`}>
          A esas filas no puedo reconocerlas si vuelves a subir el mismo
          archivo: <strong>se duplicarían</strong>. Si vas a reimportar,
          ponles el RFC o el número de cliente antes.
        </NotaExplicativa>
      )}

      {d.credito_ignorado > 0 && (
        <NotaExplicativa titulo={`${d.credito_ignorado} traen crédito y entrarán sin él`}>
          Tu archivo les pone un límite de crédito, pero tú no tienes permiso
          para fijar crédito. Entran como clientes de contado; pídele a quien
          lleva la gerencia que se lo ponga después.
        </NotaExplicativa>
      )}

      {d.campos_a_pisar > 0 && (
        <NotaExplicativa titulo={`${d.campos_a_pisar} datos que ya tenías se cambiarían`}>
          Abajo, en <strong>«Se actualizarían»</strong>, ves cuáles y qué dicen
          ahora. Si alguno lo corrigió alguien a mano, este archivo lo va a
          pisar. Queda apuntado qué decía antes, pero es más fácil pararlo
          aquí.
        </NotaExplicativa>
      )}

      <Grupo titulo="Se darían de alta" filas={d.muestra?.CREAR}
             total={t.crear} tono="text-green-700" />
      <Grupo titulo="Se actualizarían" filas={d.muestra?.ACTUALIZAR}
             total={t.actualizar} tono="text-blue-700" conCambios />
      {t.actualizar > 0 && <Ayuda id="importador.vaciar" />}
      <Grupo titulo="No entrarían" filas={d.muestra?.RECHAZAR}
             total={t.rechazar} tono="text-red-700" conMotivo />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3
                        text-sm text-red-800">{error}</div>
      )}

      <div className="sticky bottom-0 flex flex-wrap justify-end gap-2
                      border-t border-gray-200 bg-white py-3">
        <button type="button" onClick={onDescartar} disabled={aplicando}
                className="inline-flex items-center gap-2 px-4 py-3
                           font-semibold text-gray-600">
          <X className="h-4 w-4" aria-hidden="true" />
          Descartar
        </button>
        <button type="button" onClick={onAplicar} disabled={aplicando || nada}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-3
                           font-semibold text-white bg-[var(--color-marca)]
                           disabled:bg-gray-300">
          {aplicando
            ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            : <Check className="h-5 w-5" aria-hidden="true" />}
          {nada
            ? (yaEntro ? 'Este archivo ya entró' : 'No hay nada que guardar')
            : `Sí, guardar ${t.crear + t.actualizar}`}
        </button>
      </div>
      <Ayuda id="importador.ensayo_caduca" />
      {/* Botón apagado CON LA RAZÓN A LA VISTA, nunca en silencio. */}
      {nada && !yaEntro && (
        <p className="text-right text-sm text-amber-700">
          Ninguna fila de tu archivo se puede guardar. Revisa los motivos de
          arriba, corrige el archivo y vuelve a subirlo.
        </p>
      )}
      {yaEntro && (
        <p className="text-right text-sm text-amber-700">
          Este mismo archivo ya se aplicó (lote {d.ya_aplicado.id}). Para no
          duplicar nada, no se vuelve a guardar.
        </p>
      )}
    </div>
  );
}

// EL ÁRBOL ANTES DE APLICAR (ubicaciones). Una línea por ubicación que va
// a entrar, con su ruta entera por nombres. Los niveles que salen de una
// «Ruta» y no venían como fila propia se marcan: son los que el archivo
// no nombró y TEGO va a crear.
function ArbolResultante({ arbol }) {
  return (
    <section className="rounded-xl border border-gray-200">
      <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">
        Así quedaría el árbol ({arbol.length})
      </h2>
      <ul className="divide-y divide-gray-100 text-sm">
        {arbol.map((n) => (
          <li key={n.codigo} className="flex flex-wrap items-center gap-x-2 px-3 py-1.5">
            <span className="font-mono text-gray-500">{n.codigo}</span>
            <span className="text-gray-900">{n.ruta}</span>
            {n.de_la_ruta && (
              <span className="rounded bg-amber-50 px-1.5 text-xs text-amber-800">
                nivel que faltaba: se crea
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Tarjeta({ n, etiqueta, color }) {
  const tonos = {
    verde: 'border-green-200 bg-green-50 text-green-800',
    azul: 'border-blue-200 bg-blue-50 text-blue-800',
    rojo: 'border-red-200 bg-red-50 text-red-800',
  };
  return (
    <div className={`rounded-xl border p-3 ${tonos[color]}`}>
      <div className="text-2xl font-semibold">{n}</div>
      <div className="text-sm">{etiqueta}</div>
    </div>
  );
}

function Grupo({ titulo, filas, total, tono, conMotivo, conCambios }) {
  if (!total) return null;
  const lista = filas || [];
  return (
    <section className="rounded-xl border border-gray-200">
      <h2 className={`border-b border-gray-200 px-3 py-2 font-semibold ${tono}`}>
        {titulo} ({total})
      </h2>
      <ul className="divide-y divide-gray-100">
        {lista.map((f) => (
          <li key={f.fila} className="flex flex-col gap-0.5 px-3 py-2 text-sm">
            <span className="text-gray-900">
              <span className="text-gray-400">Fila {f.fila} · </span>
              {f.resumen}
            </span>
            {conMotivo && f.motivo && (
              <span className="text-red-700">{f.motivo}</span>
            )}
            {conCambios && <Cambios detalle={f.cambios_detalle} cambios={f.cambios} />}
          </li>
        ))}
      </ul>
      {total > lista.length && (
        <p className="px-3 py-2 text-sm text-gray-500">
          …y {total - lista.length} más. Se muestran las primeras {lista.length}.
        </p>
      )}
    </section>
  );
}

// QUÉ SE PISA, CAMPO POR CAMPO Y CON EL VALOR ANTERIOR A LA VISTA.
//
// «Cambia: telefono» no deja decidir nada. «Teléfono: 477 111 1111 →
// 477 999 0000» sí: es donde alguien frena y dice «ese teléfono lo
// corregí yo la semana pasada». Actualizar en masa es justo donde se
// pierde información sin que nadie lo note, y esta línea es la que lo
// hace notar ANTES, no tres meses después.
function Cambios({ detalle, cambios }) {
  // Las importaciones guardadas antes de que esto existiera solo tienen
  // el diccionario viejo; se enseñan igual, sin el valor anterior.
  const lista = detalle
    || Object.entries(cambios || {}).map(([campo, despues]) => ({
      campo, etiqueta: campo, antes: null, despues,
    }));
  if (lista.length === 0) {
    return <span className="text-gray-500">Sin cambios: ya está igual.</span>;
  }
  return (
    <ul className="text-gray-600">
      {lista.map((c) => (
        <li key={c.campo}>
          <span className="font-medium">{c.etiqueta}:</span>{' '}
          {c.antes === null || c.antes === undefined
            ? <span className="text-gray-400">estaba vacío</span>
            : <span className="text-amber-800">{c.antes}</span>}
          {' → '}
          {c.despues === 'VACIAR'
            ? <span className="font-medium text-amber-800">se vaciará</span>
            : <span className="text-gray-900">{c.despues}</span>}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------- «esta columna es…»
//
// LA COLUMNA QUE NO SE ENTIENDE ES UNA PREGUNTA, NO UN AVISO.
//
// Antes esto era una nota que decía «no reconocí CVE ARTIC; si te hace
// falta, dímelo y la añadimos» — o sea, escríbele a alguien y espera.
// Quien está a media importación un viernes no escribe a nadie: borra la
// columna del Excel o se rinde.
//
// Y se recuerda por empresa: la segunda vez que suba su catálogo, esa
// columna ya viene puesta. Sin memoria sería el mismo trabajo cada mes.
export function MapeoManual({ d, onCorregir }) {
  const [pares, setPares] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState('');
  const hay = Object.values(pares).filter(Boolean).length;

  async function enseñar() {
    setGuardando(true);
    setFallo('');
    try {
      const limpio = Object.fromEntries(
        Object.entries(pares).filter(([, v]) => v));
      const r = await api.post(`/importar/${d.id}/mapeo`, limpio);
      onCorregir(r);
    } catch (e) {
      setFallo(e.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <h2 className="font-semibold text-amber-900">
        {d.columnas_ignoradas.length === 1
          ? 'Hay una columna que no reconocí'
          : `Hay ${d.columnas_ignoradas.length} columnas que no reconocí`}
      </h2>
      <p className="text-sm text-amber-800">
        Dime qué son y las importo. <strong>Solo hace falta una vez</strong>:
        la próxima vez que subas un archivo con esas columnas, ya vendrán
        puestas.
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {d.columnas_ignoradas.map((col) => (
          <li key={col} className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">«{col}»</span>
            <span className="text-sm text-gray-600">es</span>
            <select
              value={pares[col] || ''}
              onChange={(e) => setPares({ ...pares, [col]: e.target.value })}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
              <option value="">— déjala fuera —</option>
              {(d.campos_disponibles || []).map((c) => (
                <option key={c.campo} value={c.campo}>{c.etiqueta}</option>
              ))}
            </select>
          </li>
        ))}
      </ul>
      {fallo && <p className="mt-2 text-sm text-red-700">{fallo}</p>}
      <button
        type="button" onClick={enseñar} disabled={guardando || !hay}
        className="mt-3 inline-flex items-center gap-2 rounded-xl
                   bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold
                   text-white disabled:bg-gray-300">
        {guardando
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <ListChecks className="h-4 w-4" aria-hidden="true" />}
        {hay ? `Usar ${hay === 1 ? 'esa columna' : `esas ${hay} columnas`}`
             : 'Elige arriba qué es cada una'}
      </button>
      {/* Botón apagado CON LA RAZÓN A LA VISTA. */}
      {!hay && (
        <p className="mt-1 text-sm text-amber-700">
          Si ninguna te hace falta, no toques nada: el resto se importa igual.
        </p>
      )}
    </section>
  );
}

// ------------------------------------------- los códigos de invitación
export function CodigosDeInvitacion({ filas, onCerrar }) {
  return (
    <section className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
      <h2 className="font-semibold text-amber-900">
        Estos {filas.length} códigos se ven UNA sola vez
      </h2>
      <p className="text-sm text-amber-800">
        Cada persona entra con su código y pone su propio PIN.
        <strong> Descárgalos ahora</strong>: no se pueden volver a ver, y si
        se pierden hay que cancelar las invitaciones y empezar de nuevo.
      </p>
      <ul className="mt-2 divide-y divide-amber-200 rounded-lg bg-white">
        {filas.map((f) => (
          <li key={f.telefono} className="flex flex-wrap gap-x-3 px-3 py-2 text-sm">
            <span className="font-medium text-gray-900">{f.nombre}</span>
            <span className="text-gray-500">{f.telefono}</span>
            <span className="text-gray-500">{etiquetaRol(f.rol)}</span>
            <span className="ml-auto font-mono font-semibold text-gray-900">
              {f.codigo}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            const lineas = ['Nombre,Teléfono,Puesto,Código,Enlace']
              .concat(filas.map((f) => [f.nombre, f.telefono, f.rol, f.codigo,
                                        f.enlace].join(',')));
            guardarArchivo(
              new Blob([lineas.join('\n')], { type: 'text/csv;charset=utf-8' }),
              'codigos-de-invitacion.csv');
          }}
          className="inline-flex items-center gap-2 rounded-xl
                     bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
          <Download className="h-4 w-4" aria-hidden="true" />
          Descargar la lista
        </button>
        <button type="button" onClick={onCerrar}
                className="px-3 py-2 text-sm font-semibold text-gray-600">
          Ya los guardé, ocultar
        </button>
      </div>
    </section>
  );
}

// ----------------------------------------------------------- historial
function Historial({ filas, onRecargar }) {
  if (!filas.length) {
    return (
      <EstadoVacio
        icono={FileSpreadsheet}
        titulo="Todavía no has importado nada"
        descripcion="Cuando subas un archivo, aquí queda apuntado quién lo subió, cuándo y cuántas filas entraron — y el archivo original se guarda para poder consultarlo después."
      />
    );
  }
  return (
    <section className="flex flex-col gap-2">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <History className="h-4 w-4" aria-hidden="true" />
        Lo que se ha importado
      </h2>
      <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
        {filas.map((f) => <Renglon key={f.id} f={f} onRecargar={onRecargar} />)}
      </ul>
    </section>
  );
}

function Renglon({ f, onRecargar }) {
  const [cambios, setCambios] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState(false);
  const avisos = useAvisos();

  async function deshacer() {
    setDeshaciendo(true);
    try {
      const r = await api.post(`/importar/${f.id}/deshacer`, {});
      avisos.exito(r.mensaje);
      setConfirmando(false);
      onRecargar?.();
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setDeshaciendo(false);
    }
  }

  async function verQueCambio() {
    if (cambios) { setCambios(null); return; }
    setCargando(true);
    try {
      setCambios(await api.get(`/importar/${f.id}/cambios`));
    } finally {
      setCargando(false);
    }
  }

  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3">
        <span className="font-medium text-gray-900">{f.nombre_archivo}</span>
        <span className="text-gray-500">
          {f.quien} · <TiempoRelativo valor={f.creado_en} />
        </span>
        <span className={f.estado === 'APLICADA' ? 'text-green-700'
          : f.estado === 'DESCARTADA' || f.estado_pantalla === 'CADUCADO'
            ? 'text-gray-500' : 'text-amber-700'}>
          {f.estado === 'APLICADA'
            ? `${f.filas_creadas} nuevos, ${f.filas_actualizadas} actualizados`
            : f.estado === 'DESCARTADA' ? 'Descartada'
              : f.estado_pantalla === 'CADUCADO'
                ? 'Ensayo caducado: súbelo otra vez para aplicarlo'
                : 'Ensayado, sin confirmar'}
        </span>
        {f.aplicado_por && f.aplicado_por !== f.quien && (
          <span className="text-gray-500">· aplicó {f.aplicado_por}</span>
        )}
        {f.filas_rechazadas > 0 && (
          <span className="text-red-700">{f.filas_rechazadas} rechazadas</span>
        )}
        {f.deshecha_en && (
          <span className="text-gray-500">
            · deshecha <TiempoRelativo valor={f.deshecha_en} />
          </span>
        )}
        {/* EL RESUMEN, PARA MANDÁRSELO A QUIEN MANTIENE EL ARCHIVO.
            Cuando de 500 filas se rechazan 120, lo que hace falta no es
            mirarlas en pantalla: es enviárselas a quien las puede
            arreglar. Lleva TODAS las filas, también las que entraron,
            porque la otra pregunta que se hace siempre es «¿y aquel
            cliente, entró?». */}
        <button
          type="button"
          onClick={async () => {
            guardarArchivo(await api.bajar(`/importar/${f.id}/resumen.xlsx`),
                           `resumen-importacion-${f.id}.xlsx`);
          }}
          className="ml-auto inline-flex items-center gap-1 font-semibold
                     text-[var(--color-marca)]">
          <Download className="h-3 w-3" aria-hidden="true" />
          Bajar el resumen
        </button>
        <button
          type="button"
          onClick={async () => {
            guardarArchivo(await api.bajar(`/importar/${f.id}/archivo`),
                           f.nombre_archivo);
          }}
          className="inline-flex items-center gap-1 font-semibold
                     text-[var(--color-marca)]">
          Ver el archivo
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>

      {/* DESHACER PIDE CONFIRMACIÓN Y EXPLICA LA CONSECUENCIA EN UNA
          FRASE, porque retira de golpe todo lo que entró con ese
          archivo. Y no aparece si ya se deshizo: un botón que va a
          fallar no se ofrece. */}
      {f.estado === 'APLICADA' && !f.deshecha_en && (
        <div className="mt-1">
          {!confirmando ? (
            <button type="button" onClick={() => setConfirmando(true)}
                    className="inline-flex items-center gap-1 font-semibold
                               text-gray-600">
              <Undo2 className="h-3 w-3" aria-hidden="true" />
              Deshacer esta importación
            </button>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-amber-900">
                Se van a retirar <strong>los {f.filas_creadas} que entraron
                con este archivo</strong>
                {f.campos_pisados > 0
                  ? <> y se devolverán {f.campos_pisados} datos a como
                      estaban.</>
                  : '.'}
              </p>
              <p className="mt-1 text-amber-800">
                Nada se borra: lo creado queda retirado y se puede volver a
                activar. Lo que alguien haya corregido a mano después de
                importar <strong>no se toca</strong>.
              </p>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={deshacer} disabled={deshaciendo}
                        className="inline-flex items-center gap-2 rounded-xl
                                   bg-amber-600 px-3 py-2 font-semibold
                                   text-white disabled:bg-gray-300">
                  {deshaciendo
                    ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    : <Undo2 className="h-4 w-4" aria-hidden="true" />}
                  Sí, deshacerla
                </button>
                <button type="button" onClick={() => setConfirmando(false)}
                        className="px-3 py-2 font-semibold text-gray-600">
                  Mejor no
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* GUARDAR EL ARCHIVO CONTESTA «QUÉ SE SUBIÓ». Esto contesta «y qué
          le hizo eso a mis datos», que es la pregunta que se hace tres
          meses después, cuando un teléfono está mal y nadie recuerda
          haberlo tocado. */}
      {f.campos_pisados > 0 && (
        <div className="mt-1">
          <button
            type="button" onClick={verQueCambio} disabled={cargando}
            className="inline-flex items-center gap-1 font-semibold
                       text-[var(--color-marca)]">
            {cargando
              ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              : <ListChecks className="h-3 w-3" aria-hidden="true" />}
            {cambios
              ? 'Ocultar qué cambió'
              : `Ver qué cambió (${f.campos_pisados} datos)`}
          </button>

          {cambios && (
            <ul className="mt-2 flex flex-col gap-2 rounded-xl bg-gray-50 p-3">
              {cambios.registros.map((r) => (
                <li key={r.id_entidad}>
                  <span className="font-medium text-gray-900">{r.quien}</span>
                  {r.fila && (
                    <span className="text-gray-400"> · fila {r.fila} del archivo</span>
                  )}
                  <Cambios detalle={r.cambios} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
